import { useState, useRef, useEffect, useCallback } from "react";

/* ═══════════════════════════════════════════════════════════
   YOURS TRULY — AppV3: SUPERNOVA + EMAIL CAPTURE

   Desktop: 40% chat / 60% supernova, side by side.
   Mobile:  Chat | ✦ Star tab toggle (the star gets full screen).
   The giver pours out their person; the star fills with vivid,
   specific things; once there's enough they enter their email
   and it goes to the atelier. Brief extracted silently. Dark
   by default. No Wrapped reveal, no select screen.
   ═══════════════════════════════════════════════════════════ */

const API = "/api/chat";
const API_SECRET = "yt_sculpt_2026_beta";
const FORMSPREE = "https://formspree.io/f/xrerqqje";

const GHOST_PROMPTS = ["an obsession", "a place", "what they love", "a creature"];
const NODE_HUES = ["#f3c46b", "#ff9e6d", "#ff6f91", "#c98be6", "#8aa2ff", "#ffb15c", "#ff85b3"];

function useIsMobile(bp = 900) {
  const [m, setM] = useState(typeof window !== "undefined" && window.innerWidth <= bp);
  useEffect(() => { const h = () => setM(window.innerWidth <= bp); window.addEventListener("resize", h); return () => window.removeEventListener("resize", h); }, [bp]);
  return m;
}

/* ═══ THEMES (chat side) ═══ */
const DARK = {
  bg: "#0a0a14", panel: "rgba(8,8,18,0.95)", text: "#e8e6e2", textSoft: "rgba(232,230,226,0.65)",
  textMuted: "rgba(200,200,220,0.35)", textFaint: "rgba(200,200,220,0.15)",
  aiBubble: "rgba(255,255,255,0.04)", aiBorder: "rgba(255,255,255,0.06)",
  userBubble: "rgba(201,169,110,0.08)", userBorder: "rgba(201,169,110,0.15)",
  inputBg: "rgba(255,255,255,0.04)", inputBorder: "rgba(201,169,110,0.12)",
  border: "rgba(255,255,255,0.06)", accent: "#c9a96e", gold: "rgba(201,169,110,",
  coreBg: "rgba(201,169,110,0.05)", coreBorder: "rgba(201,169,110,0.12)", coreText: "rgba(201,169,110,0.7)",
  successBg: "rgba(80,200,120,0.04)", successBorder: "rgba(80,200,120,0.15)", successText: "rgba(80,200,120,0.65)",
  toggle: { bg: "rgba(255,255,255,0.06)", color: "rgba(200,200,220,0.5)" },
};
const LIGHT = {
  bg: "#e8e4ee", panel: "rgba(240,236,244,0.95)", text: "#2a2830", textSoft: "rgba(42,40,48,0.6)",
  textMuted: "rgba(42,40,48,0.35)", textFaint: "rgba(42,40,48,0.15)",
  aiBubble: "rgba(255,255,255,0.7)", aiBorder: "rgba(160,156,170,0.12)",
  userBubble: "rgba(160,130,70,0.08)", userBorder: "rgba(160,130,70,0.15)",
  inputBg: "rgba(255,255,255,0.65)", inputBorder: "rgba(160,130,70,0.15)",
  border: "rgba(160,156,170,0.12)", accent: "#a08046", gold: "rgba(160,130,70,",
  coreBg: "rgba(160,130,70,0.06)", coreBorder: "rgba(160,130,70,0.12)", coreText: "rgba(120,90,40,0.7)",
  successBg: "rgba(60,160,90,0.05)", successBorder: "rgba(60,160,90,0.15)", successText: "rgba(60,140,80,0.65)",
  toggle: { bg: "rgba(0,0,0,0.05)", color: "rgba(80,78,90,0.5)" },
};

const SKY = `radial-gradient(circle at 50% 44%, rgba(255,244,214,0.30), transparent 13%),
             radial-gradient(circle at 50% 44%, rgba(255,150,95,0.24), transparent 28%),
             radial-gradient(circle at 50% 44%, rgba(226,84,150,0.18), transparent 45%),
             radial-gradient(circle at 50% 44%, rgba(120,86,210,0.16), transparent 63%),
             radial-gradient(ellipse at 50% 44%, #0c0a1a 60%, #050409 92%)`;

const STARS = [
  {t:"10%",l:"16%",s:2,d:0},{t:"20%",l:"74%",s:1.5,d:0.6},{t:"32%",l:"28%",s:1,d:1.2},
  {t:"14%",l:"52%",s:1,d:0.3},{t:"44%",l:"84%",s:2,d:0.9},{t:"58%",l:"20%",s:1.5,d:1.5},
  {t:"68%",l:"62%",s:1,d:0.4},{t:"76%",l:"38%",s:2,d:1.1},{t:"26%",l:"90%",s:1,d:0.7},
  {t:"84%",l:"72%",s:1.5,d:0.2},{t:"38%",l:"8%",s:1,d:1.3},{t:"88%",l:"30%",s:1,d:0.5},
  {t:"6%",l:"40%",s:1,d:0.95},{t:"62%",l:"88%",s:1.5,d:1.4},
];

/* ═══ CONVERSATION PROMPT — hunts the deep layer (trait/value/story), not a like-list ═══ */
function getConversationPrompt() {
  return `You are the Sculptor - a quiet, perceptive artist who is going to turn someone the giver loves into a one-of-one sculpture. You are not a form to fill out. You are a person who is genuinely curious about the human being on the other side of this gift.

YOUR REAL GOAL: by the end of this short conversation you must be able to finish this sentence in your own words - "This person is someone who ___." That blank is a TRAIT, a VALUE, or a STORY: who they ARE, not what they like. That deep layer is the only thing a sculpture can be built from. You ALSO collect 2-3 specific LIKES (a team, an instrument, a pet, a food, a place) - but these are secondary: they are the details you weave INTO the form later, not the point. A pile of likes with NO trait is a FAILED conversation no matter how many you collect.

VOICE (matters most): keep EVERY message SHORT. One quick warm line, then ONE short question. NEVER a paragraph. Never gush. Think texting a close friend, not writing an essay.

OPEN: get their name and who they are to the giver. Then invite the pour: "Tell me about [name] - what are they like?"

THE THREE LAYERS - hunt them in this order of depth (one real one beats ten likes):
1. TRAIT (who they are / how they show up): "what's [name] actually like - the thing everyone who knows them would agree on?", "what would you notice in the first five minutes?", "what's their energy when they walk into a room?"
2. VALUE (what they care about most): "what do they care about more than almost anything?", "what would they never compromise on?"
3. STORY (a moment that is pure them): "tell me a moment that's so them", "when you picture them, what are they doing?"

THE ONE MOVE THAT MAKES OR BREAKS THIS: when the giver hands you a LIKE ("he loves United, he's a foodie, big into horror"), that is your CUE TO GO DEEPER, not to move on. Turn every like into a doorway to a trait. "A United man - and what's he like watching a match: all-in and loud, or quiet and intense?" Never just collect the like and move to the next one. The like is the surface; you are fishing for the person under it.

ALSO CATCH THE GOLD - when you hear any of these, pull that ONE thread; it is worth more than any object:
- A CONTRADICTION ("calm guy, but a huge laugh") - name it back warmly; contradictions are the most sculptural thing a person has.
- THE GIVER'S OWN LONGING / REGRET ("we don't talk like we used to") - this is what the gift is secretly about; lean in softly.
- PAST TENSE ("he used to play guitar") - "does he still? what changed?"

DON'T: ask boring routine questions (age, job, address) unless they clearly lead to who the person is. Don't prop-hunt (whether they OWN a jersey/watch) - meaning is enough. Don't drill the same vein twice.

CAPTURE QUALITY: keep only SPECIFIC, evocative likes, never generic props. "United" not "football". "His grandfather's watch" not "a watch". Drop generic items (a phone, a couch, a TV).

WHEN TO WRAP - only when BOTH are true: (1) you can name who this person IS in one sentence - a trait, value, or story with real feeling behind it - AND (2) you have 2-3 specific likes to weave in. If you have likes but NO trait, you are NOT done: ask one more real question about who they are. Do NOT wrap on a like-list. When genuinely done, write ONE warm closing line that reflects who you now understand them to be (prove you SAW them), point them to their email - "That's them, I can see it - drop your email whenever you're ready." - ask NO further question, and on that wrap message ONLY set "ready":true. Every other turn "ready":false. Aim for five or six exchanges, never a dozen.

You are quietly filling a constellation:
- name: the recipient's name
- who_they_are: ONE sentence naming the trait/value/story - who they ARE (this is the most important field; if you cannot fill it with real feeling, you are not ready to wrap)
- objects: 2-3 specific evocative likes to weave in (secondary)
- essence: their energy, contradictions, longings you caught
- gesture_hint: the single motion that IS them (a surge, a gathering, a crescendo, an unfurling, a stillness)

EVERY TURN return your cumulative read of all of these.

FORMAT - ONLY valid JSON, no markdown:
{"message":"short warm line + one short question","captured":{"name":"...","who_they_are":"...","objects":["..."],"essence":["..."],"gesture_hint":"..."},"ready":false}`;
}

/* ═══ EXTRACTION PROMPT — runs silently on submit; same iceberg shape ═══ */
const SYS_EXTRACT = `You build the sculpture brief for Yours Truly, a one-of-one personalized sculpture studio. You are given a CONVERSATION in which a giver poured out everything about a person they love.

Organize it into a brief for a human artist. You do NOT design the sculpture: you never choose the final gesture, pick the form, or write any image prompt. You prepare the leap.

PRINCIPLES (hard rules):
- Symbols over objects. Extract what things MEAN, never the literal object or trademark. A team is loyalty, not a badge. A film/game becomes the one thing the giver wants from it, never the title in letters. Never keep a generic prop; keep the specific meaningful thing.
- Form-givers vs details. Some elements can supply the whole gesture (occupation, an animal or plant's natural motion, a core truth). Others only live inside the form (a team, an instrument, a place). Flag which is which; never promote a detail to the form.
- Restraint: three or four elements woven cleanly beats six crowded. Where two carry the same idea, recommend dissolving one into material, color, or light.
- The element the giver cares most about is a candidate for the heart-position. The thing the giver wishes they could say is the emotional core.

OUTPUT - ONLY valid JSON, no markdown fences:
{
  "name":"recipient name",
  "relationship":"giver's relationship to them",
  "occasion":"occasion if mentioned, else empty",
  "who_they_are":"ONE sentence naming the TRAIT, VALUE, or STORY - who this person fundamentally IS, not what they like. This is the input the artist builds the FORM from. If the conversation only yielded likes with no real trait/value/story, leave this EMPTY - an empty value is the signal that the conversation failed and the design will be generic.",
  "tier1":[{"id":"t1_1","text":"a specific visual element to weave in","category":"object"}],
  "tier2":[{"id":"t2_1","text":"a quirk / energy / contradiction that shapes the form","category":"identity"}],
  "tier3":[{"id":"t3_1","text":"a deep truth about what this says","category":"theme"}],
  "emotional_core":"one sentence - who they are / the feeling",
  "gift_subtext":"the unsaid line, first person, lightly polished",
  "creative_notes":{
    "the_build":"ONE sentence the artist reads first: the single gesture + how the top elements weave into it (e.g. 'An outpouring that returns — a forward-leaning form whose released light curves back to a glowing recessed heart').",
    "candidate_gestures":["1-3 motions, each with one line of why - SUGGESTIONS only"],
    "form_giver_source":"which element most naturally supplies the overall FORM, and why",
    "heart_element":"the single element best for the recessed central point, and why",
    "element_roles":["for each main element, tag how it should live in the piece: 'Man United -> MATERIAL (the colour of the crystal)', 'Nolan -> INTERNAL (hidden faceted structure)', 'guitar -> FORM (the lean itself)', 'place -> DETAIL'. Roles: FORM / MATERIAL / INTERNAL / DETAIL."],
    "dissolve_or_drop":["redundant elements or ones that should become material/color/light"],
    "watch_outs":["anything that risks becoming a literal object-on-a-shelf"]
  }
}

Counts: tier1 = 4-6, tier2 = 3-5, tier3 = 2-3. Category must be one of: object, ritual, identity, place, memory, emotion, insight, symbolism, theme, texture, contradiction. Specific and warm, never generic. Silently correct obvious misspellings of real proper nouns (e.g. "Chrisopher Noland" -> "Christopher Nolan", "Man U" -> "Manchester United"). If the conversation is thin on something, say so rather than inventing.`;

function safeParseAI(raw) {
  try { return JSON.parse(raw.replace(/```json\s*/g, "").replace(/```\s*/g, "").trim()); }
  catch { const m = raw.match(/\{[\s\S]*\}/); if (m) try { return JSON.parse(m[0]); } catch {} return { message: raw, suggestions: [] }; }
}

function dedupe(arr) {
  const seen = new Set(); const out = [];
  (arr || []).forEach(x => { const k = String(x).trim().toLowerCase(); if (x && !seen.has(k)) { seen.add(k); out.push(x); } });
  return out;
}

/* ═══ SUPERNOVA (full panel; same on desktop side and mobile Star tab) ═══ */
function Supernova({ captured, name, relationship, isMobile, email, onEmail, onSubmit, submitting }) {
  const objs = (captured.objects || []).slice(0, 7);
  const need = Math.max(0, 3 - objs.length);
  const ghosts = GHOST_PROMPTS.slice(0, need);
  const enough = objs.length >= 2;
  const star = name || "…";
  const valid = email.includes("@") && email.includes(".");

  const R = isMobile ? 100 : 162;
  const coreSize = isMobile ? 27 : 34;
  const bubbleMax = isMobile ? 104 : 150;

  const emailBlock = enough ? (
    <div style={{ animation: "fadeUp 0.5s ease" }}>
      <div style={{ textAlign: "center", fontSize: 12, color: "rgba(255,235,210,0.75)", fontStyle: "italic", fontFamily: '"Cormorant Garamond", serif', marginBottom: 10 }}>
        {objs.length >= 3 ? `That's plenty — where do we send ${name || "them"}?` : `Where do we send ${name || "them"}?`}
      </div>
      <div style={{ display: "flex", gap: 7 }}>
        <input value={email} onChange={e => onEmail(e.target.value)} onKeyDown={e => { if (e.key === "Enter" && valid && !submitting) onSubmit(); }} placeholder="your@email.com" type="email" inputMode="email" style={{
          flex: 1, padding: "13px 14px", borderRadius: 12, border: "1px solid rgba(255,200,140,0.3)", background: "rgba(255,255,255,0.06)", color: "#fff", fontSize: 16, fontFamily: '"Outfit", sans-serif',
        }} />
        <button onClick={onSubmit} disabled={!valid || submitting} style={{
          padding: "13px 18px", borderRadius: 12, border: "1px solid rgba(255,205,140,0.5)", cursor: valid && !submitting ? "pointer" : "default",
          background: valid ? "linear-gradient(180deg, rgba(255,200,120,0.26), rgba(255,140,90,0.16))" : "rgba(255,255,255,0.04)",
          color: valid ? "#fff3df" : "rgba(255,255,255,0.3)", fontSize: 12.5, fontWeight: 600, letterSpacing: "0.06em", whiteSpace: "nowrap",
          boxShadow: valid ? "0 0 24px rgba(255,170,90,0.3)" : "none",
        }}>{submitting ? "Sending…" : "✦ Send"}</button>
      </div>
    </div>
  ) : (
    <div style={{ textAlign: "center", fontSize: 11.5, color: "rgba(255,255,255,0.32)", fontStyle: "italic", fontFamily: '"Cormorant Garamond", serif' }}>Tell me a little more, and the star ignites…</div>
  );

  const nodes = [
    ...objs.map((o, i) => ({ label: o, real: true, hue: NODE_HUES[i % NODE_HUES.length] })),
    ...ghosts.map(g => ({ label: g, real: false })),
  ];

  return (
    <div style={{ flex: 1, minHeight: 0, width: "100%", position: "relative", overflow: "hidden", background: SKY, display: "flex", flexDirection: "column", borderLeft: isMobile ? "none" : "1px solid rgba(255,200,140,0.1)" }}>
      {STARS.map((s, i) => (
        <div key={i} style={{ position: "absolute", top: s.t, left: s.l, width: s.s, height: s.s, borderRadius: "50%", background: "rgba(255,240,210,0.9)", boxShadow: "0 0 6px rgba(255,220,170,0.7)", animation: `twinkle 3.4s ease-in-out ${s.d}s infinite` }} />
      ))}

      <div style={{ padding: isMobile ? "16px 20px 0" : "22px 26px 0", position: "relative", zIndex: 3 }}>
        <div style={{ fontSize: 8, letterSpacing: "0.3em", textTransform: "uppercase", color: "rgba(255,225,190,0.5)" }}>The constellation of</div>
      </div>

      <div style={{ flex: 1, position: "relative", minHeight: isMobile ? 300 : 340 }}>
        <div style={{ position: "absolute", left: "50%", top: "44%", width: 260, height: 260, transform: "translate(-50%,-50%)", borderRadius: "50%", background: "radial-gradient(circle, rgba(255,210,150,0.30), rgba(255,140,90,0.11) 45%, transparent 72%)", animation: "coreflare 4.5s ease-in-out infinite", zIndex: 1 }} />
        <div style={{ position: "absolute", left: "50%", top: "44%", transform: "translate(-50%,-50%)", textAlign: "center", zIndex: 3, width: 190 }}>
          <div style={{ fontFamily: '"Cormorant Garamond", serif', fontStyle: "italic", fontSize: coreSize, color: "#fff7e6", textShadow: "0 0 26px rgba(255,205,140,0.95), 0 0 55px rgba(255,140,90,0.5)", lineHeight: 1.05 }}>{star}</div>
          {relationship && <div style={{ fontSize: 11, color: "rgba(255,225,190,0.6)", marginTop: 3, fontStyle: "italic", fontFamily: '"Cormorant Garamond", serif' }}>{relationship}</div>}
        </div>
        <div style={{ position: "absolute", left: "50%", top: "44%", width: R*2, height: R*2, transform: "translate(-50%,-50%)", borderRadius: "50%", border: "1px dashed rgba(255,220,180,0.12)", zIndex: 1 }} />
        {/* constellation lines: glowing tethers from the core out to each thing */}
        {nodes.map((n, i) => {
          const a = (i / Math.max(nodes.length, 1)) * 2 * Math.PI - Math.PI / 2;
          const deg = a * 180 / Math.PI;
          return (
            <div key={"line"+i} style={{ position: "absolute", left: "50%", top: "44%", width: R, height: n.real ? 2 : 1, transformOrigin: "0 50%", transform: `rotate(${deg}deg)`, zIndex: 2, pointerEvents: "none" }}>
              <div style={{
                width: "100%", height: "100%", transformOrigin: "left center", borderRadius: 2,
                background: n.real ? `linear-gradient(90deg, ${n.hue}00, ${n.hue}dd)` : "linear-gradient(90deg, rgba(255,255,255,0), rgba(255,255,255,0.22))",
                boxShadow: n.real ? `0 0 8px ${n.hue}99` : "none",
                animation: `grow 0.7s ease ${i*0.08}s both`,
              }} />
            </div>
          );
        })}
        {nodes.map((n, i) => {
          const a = (i / Math.max(nodes.length, 1)) * 2 * Math.PI - Math.PI / 2;
          const x = Math.cos(a) * R, y = Math.sin(a) * R;
          return (
            <div key={i} style={{ position: "absolute", left: `calc(50% + ${x}px)`, top: `calc(44% + ${y}px)`, transform: "translate(-50%,-50%)", zIndex: 4 }}>
              <div style={{
                padding: isMobile ? "7px 11px" : "8px 14px", borderRadius: 15, fontSize: isMobile ? 11.5 : 12, maxWidth: bubbleMax, textAlign: "center", lineHeight: 1.3,
                background: n.real ? `${n.hue}24` : "transparent",
                border: n.real ? `1px solid ${n.hue}aa` : "1px dashed rgba(255,255,255,0.2)",
                color: n.real ? "#fff" : "rgba(255,255,255,0.4)",
                fontStyle: n.real ? "normal" : "italic",
                boxShadow: n.real ? `0 0 20px ${n.hue}66, inset 0 0 14px ${n.hue}22` : "none",
                textShadow: n.real ? "0 1px 4px rgba(0,0,0,0.55)" : "none",
                animation: n.real ? `fadeUp 0.55s ease ${i*0.06}s both` : "none",
                backdropFilter: "blur(2px)",
              }}>{n.real ? n.label : n.label + "?"}</div>
            </div>
          );
        })}
      </div>

      <div style={{ padding: isMobile ? "8px 20px calc(env(safe-area-inset-bottom, 0px) + 22px)" : "8px 30px 26px", position: "relative", zIndex: 3 }}>
        {(captured.essence || []).length > 0 && (
          <div style={{ fontSize: 12, color: "rgba(255,235,210,0.7)", fontStyle: "italic", fontFamily: '"Cormorant Garamond", serif', lineHeight: 1.5, marginBottom: 8, textAlign: "center" }}>
            {dedupe(captured.essence).slice(0, 4).join(" · ")}
          </div>
        )}
        {captured.gesture_hint && (
          <div style={{ textAlign: "center", fontSize: 9, letterSpacing: "0.16em", textTransform: "uppercase", color: "rgba(230,170,210,0.75)", marginBottom: 16 }}>gesture forming · {captured.gesture_hint}</div>
        )}
        {emailBlock}
      </div>
    </div>
  );
}

/* ═══ MAIN ═══ */
const GREETING = "Hey \u2726 I'm your sculptor. Tell me about someone you love \u2014 who are we creating something for?";
const CHIPS = ["My mom", "My dad", "My partner", "My best friend", "My brother", "My sister"];

export default function App() {
  const isMobile = useIsMobile();
  const [isDark, setIsDark] = useState(true); // dark is the default for everyone; toggle still works
  const T = isDark ? DARK : LIGHT;

  const [phase, setPhase] = useState("chat"); // chat | done
  const [mobileTab, setMobileTab] = useState("chat"); // chat | star
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [gen, setGen] = useState(false);
  const [hist, setHist] = useState([]);
  const [sugs, setSugs] = useState(CHIPS);
  const [hasGreeted, setHasGreeted] = useState(false);

  const [captured, setCaptured] = useState({ objects: [], essence: [], gesture_hint: "" });
  const [name, setName] = useState("");
  const [relationship, setRelationship] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef(null);
  const sendRef = useRef(null);
  const flippedRef = useRef(false);
  const chatEndRef = useRef(null);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const enoughCaptured = captured.objects.length >= 2;

  const toggleVoice = useCallback(() => {
    if (isListening) { recognitionRef.current?.stop(); setIsListening(false); return; }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { alert("Voice not supported in this browser. Try Chrome or Safari."); return; }
    const rec = new SR(); rec.continuous = true; rec.interimResults = true; rec.lang = "en-US";
    let finalTranscript = ""; let silenceTimer = null;
    rec.onresult = (e) => {
      let interim = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) finalTranscript += e.results[i][0].transcript + " ";
        else interim += e.results[i][0].transcript;
      }
      setInput(finalTranscript + interim);
      clearTimeout(silenceTimer);
      silenceTimer = setTimeout(() => {
        if (finalTranscript.trim()) { rec.stop(); setIsListening(false); if (sendRef.current) sendRef.current(finalTranscript.trim()); finalTranscript = ""; }
      }, 2000);
    };
    rec.onerror = () => setIsListening(false);
    rec.onend = () => setIsListening(false);
    recognitionRef.current = rec; rec.start(); setIsListening(true);
  }, [isListening]);

  useEffect(() => {
    if (!hasGreeted) { setHasGreeted(true); setTimeout(() => {
      setMsgs([{ text: GREETING, isUser: false }]);
      setHist([{ role: "assistant", content: JSON.stringify({ message: GREETING }) }]);
      setSugs(CHIPS);
    }, 400); }
  }, [hasGreeted]);

  const submitBrief = useCallback(async () => {
    if (submitting || !(email.includes("@") && email.includes("."))) return;
    setSubmitting(true);

    /* Clean the dialogue: strip the AI's raw JSON down to its spoken line, so the
       artist reads a real conversation — "Sculptor: … / Giver: …" — not {"message":...}. */
    const cleanHist = hist.map(m => ({
      role: m.role,
      content: m.role === "assistant" ? (safeParseAI(m.content).message || m.content) : m.content,
    }));
    const convoText = cleanHist.map(m => (m.role === "user" ? "Giver: " : "Sculptor: ") + m.content).join("\n");

    /* 1) SAVE THE RAW BRIEF IMMEDIATELY — never let a slow/failed extraction lose a lead. */
    const rawPayload = {
      type: "creative_brief", email,
      name: name || "", relationship: relationship || "",
      objects: dedupe(captured.objects).join(" | "),
      essence: dedupe(captured.essence).join(" | "),
      gesture: captured.gesture_hint || "",
      conversation: convoText,
      source: "appv3_supernova_email",
    };
    const formspreePromise = fetch(FORMSPREE, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(rawPayload) })
      .then(r => { if (!r.ok) console.error('formspree failed', r.status); })
      .catch(e => console.error('formspree error', e));
    const savePromise = fetch('/api/save', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({
      brief_text: JSON.stringify({ captured, email, name, relationship, conversation: cleanHist }),
      theme: 'iceberg_final', source: 'iceberg_final',
    }) }).then(async r => { if (!r.ok) console.error('save failed', r.status, await r.text().catch(() => '')); })
      .catch(e => console.error('save error', e));

    /* 2) Show success the moment the raw saves are away. */
    await Promise.allSettled([formspreePromise, savePromise]);
    setPhase("done"); setSubmitting(false);

    /* 3) ENRICH IN THE BACKGROUND with hard retry — build the artist brief and save it.
          Retries up to 3x so a slow/garbled extraction doesn't leave an empty brief. */
    (async () => {
      const transcript = convoText;
      let iceberg = null;
      for (let attempt = 0; attempt < 3 && !iceberg; attempt++) {
        try {
          const er = await fetch(API, { method: "POST", headers: { "Content-Type": "application/json", "x-yt-key": API_SECRET },
            body: JSON.stringify({ model: "claude-sonnet-4-6", max_tokens: 1800, system: SYS_EXTRACT, messages: [{ role: "user", content: "THE CONVERSATION:\n\n" + transcript }] }) });
          const ed = await er.json(); const eraw = ed.content?.map(c => c.text || "").join("") || "";
          const data = safeParseAI(eraw);
          if (data && (data.tier1 || data.tier2 || data.tier3)) iceberg = data;
        } catch (e) { console.error('enrich attempt', attempt, e); }
        if (!iceberg) await new Promise(r => setTimeout(r, 900 * (attempt + 1)));
      }
      if (!iceberg) { console.error('enrich gave up after retries'); return; }

      fetch(FORMSPREE, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({
        type: "creative_brief_enriched", email,
        name: name || iceberg.name || "", relationship: relationship || iceberg.relationship || "",
        emotional_core: iceberg.emotional_core || "", gift_subtext: iceberg.gift_subtext || "",
        elements: (iceberg.tier1 || []).map(i => i.text).join(" | "),
        personality: (iceberg.tier2 || []).map(i => i.text).join(" | "),
        soul: (iceberg.tier3 || []).map(i => i.text).join(" | "),
        creative_notes: JSON.stringify(iceberg.creative_notes || {}),
        source: "appv3_enrich",
      }) }).catch(() => {});
      fetch('/api/save', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({
        brief_text: JSON.stringify({ iceberg, captured, email, name, relationship, conversation: cleanHist }),
        theme: 'iceberg_final', source: 'iceberg_enriched',
      }) }).catch(() => {});
    })();
  }, [submitting, email, hist, name, relationship, captured]);

  const send = useCallback(async (text) => {
    if (!text.trim() || gen) return;
    const um = text.trim();
    setInput(""); setMsgs(p => [...p, { text: um, isUser: true }]); setSugs([]); setGen(true);
    const nh = [...hist, { role: "user", content: um }];

    try {
      const r = await fetch(API, { method: "POST", headers: { "Content-Type": "application/json", "x-yt-key": API_SECRET },
        body: JSON.stringify({ model: "claude-sonnet-4-6", max_tokens: 320, system: getConversationPrompt(), messages: nh }) });
      const d = await r.json(); const raw = d.content?.map(c => c.text || "").join("") || "";
      const parsed = safeParseAI(raw);

      setMsgs(p => [...p, { text: parsed.message, isUser: false }]);
      setHist([...nh, { role: "assistant", content: raw }]);
      setSugs(parsed.suggestions || []);

      const cap = parsed.captured || {};
      setCaptured(prev => ({
        objects: dedupe([...(prev.objects || []), ...(cap.objects || [])]),
        essence: dedupe([...(prev.essence || []), ...(cap.essence || [])]),
        gesture_hint: cap.gesture_hint || prev.gesture_hint,
      }));
      if (cap.name && !name) setName(cap.name);

      /* conversation wrapped -> on mobile, slide them to the Star where the email lives */
      if (parsed.ready && !flippedRef.current) {
        flippedRef.current = true;
        setSugs([]);
        if (isMobile) setTimeout(() => setMobileTab("star"), 700);
      }
    } catch { setMsgs(p => [...p, { text: "Tell me more…", isUser: false }]); setHist(nh); }
    setGen(false);
  }, [hist, gen, name, isMobile]);
  sendRef.current = send;

  useEffect(() => {
    if (hist.length < 2) return;
    const userText = hist.filter(m => m.role === "user").map(m => m.content).join("  ");
    if (!name) {
      const m = userText.match(/\b(?:is|named|called|he'?s|she'?s|they'?re)\s+([A-Z][a-z]+)/) || userText.match(/\b([A-Z][a-z]{2,})\b/);
      const stop = ["My","The","He","She","They","His","Her","We","Dubai","USA","But","Anime","Just"];
      if (m && m[1] && !stop.includes(m[1])) setName(m[1]);
    }
    if (!relationship) {
      const rel = userText.match(/\b(mom|dad|mother|father|partner|wife|husband|girlfriend|boyfriend|best friend|brother|sister|son|daughter|friend)\b/i);
      if (rel) setRelationship(rel[0].toLowerCase());
    }
  }, [hist, name, relationship]);

  /* ─── chat column (used on desktop 40% and mobile Chat tab) ─── */
  const chatColumn = (
    <div style={{ width: isMobile ? "100%" : "40%", flex: isMobile ? 1 : undefined, flexShrink: 0, display: "flex", flexDirection: "column", overflow: "hidden", minWidth: 0, minHeight: 0 }}>
      <div style={{ flex: 1, overflow: "auto", WebkitOverflowScrolling: "touch", padding: isMobile ? "16px 16px" : "24px 28px", width: "100%" }}>
        {msgs.map((m, i) => (
          <div key={i} style={{ display: "flex", justifyContent: m.isUser ? "flex-end" : "flex-start", marginBottom: 10, animation: "fadeUp 0.3s ease" }}>
            <div style={{ maxWidth: "85%", padding: "12px 16px", borderRadius: m.isUser ? "18px 18px 4px 18px" : "18px 18px 18px 4px", background: m.isUser ? T.userBubble : T.aiBubble, border: `1px solid ${m.isUser ? T.userBorder : T.aiBorder}`, color: T.text, fontSize: isMobile ? 15 : 14, lineHeight: 1.6 }}>{m.text}</div>
          </div>
        ))}
        {gen && <div style={{ marginBottom: 10 }}><div style={{ display: "inline-flex", padding: "13px 18px", gap: 5, borderRadius: "18px 18px 18px 4px", background: T.aiBubble, border: `1px solid ${T.aiBorder}` }}>{[0,1,2].map(i => <div key={i} style={{ width: 5, height: 5, borderRadius: "50%", background: T.accent, opacity: 0.5, animation: `pulse 1.2s ease-in-out ${i*0.2}s infinite` }} />)}</div></div>}
        <div ref={chatEndRef} />
      </div>

      {/* mobile: nudge to the Star tab once there's enough to finish */}
      {isMobile && enoughCaptured && (
        <div style={{ padding: "0 16px 8px" }}>
          <button onClick={() => setMobileTab("star")} style={{ width: "100%", padding: "12px", borderRadius: 12, border: "1px solid rgba(201,169,110,0.4)", background: "rgba(201,169,110,0.12)", color: T.accent, fontSize: 12.5, fontWeight: 600, letterSpacing: "0.06em", cursor: "pointer" }}>✦ Ready — open the Star to finish</button>
        </div>
      )}

      {sugs?.length > 0 && (
        <div style={{ padding: "0 16px 6px", width: "100%", display: "flex", flexWrap: "wrap", gap: 6 }}>
          {sugs.map((s, i) => (
            <button key={i} onClick={() => send(s)} style={{ padding: "9px 16px", borderRadius: 20, border: isDark ? "1px solid rgba(255,255,255,0.15)" : "1px solid rgba(60,50,70,0.2)", background: isDark ? "rgba(255,255,255,0.07)" : "rgba(255,255,255,0.9)", color: isDark ? "rgba(255,255,255,0.8)" : "rgba(40,35,50,0.85)", fontSize: 12.5, cursor: "pointer", fontFamily: '"Outfit", sans-serif' }}>{s}</button>
          ))}
        </div>
      )}
      <div style={{ padding: isMobile ? "10px 16px calc(env(safe-area-inset-bottom, 0px) + 16px)" : "10px 28px 24px", width: "100%" }}>
        {isListening && <div style={{ textAlign: "center", marginBottom: 8 }}><div style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 12px", borderRadius: 12, background: "rgba(220,60,60,0.1)", border: "1px solid rgba(220,60,60,0.2)" }}><div style={{ width: 6, height: 6, borderRadius: "50%", background: "#dc3c3c", animation: "pulse 1s ease-in-out infinite" }} /><span style={{ fontSize: 10, color: "#dc3c3c" }}>Listening...</span></div></div>}
        <div style={{ display: "flex", alignItems: "flex-end", gap: 8, background: T.inputBg, border: isListening ? "1px solid rgba(220,60,60,0.3)" : `1px solid ${T.inputBorder}`, borderRadius: 22, padding: "4px 4px 4px 16px", transition: "border 0.3s ease" }}>
          <textarea value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(input); } }} placeholder={isListening ? "Speak freely..." : "Pour it all out..."} rows={1} style={{ flex: 1, background: "none", border: "none", color: T.text, fontSize: isMobile ? 16 : 14, fontFamily: '"Outfit", sans-serif', resize: "none", padding: "10px 0", lineHeight: 1.5 }} />
          <button onClick={toggleVoice} style={{ width: 40, height: 40, borderRadius: 14, border: "none", background: isListening ? "rgba(220,60,60,0.15)" : (isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)"), color: isListening ? "#dc3c3c" : T.textMuted, fontSize: 18, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{isListening ? "⏹" : "🎤"}</button>
          <button onClick={() => send(input)} disabled={!input.trim() || gen} style={{ width: 40, height: 40, borderRadius: 14, border: "none", background: input.trim() ? `${T.gold}0.15)` : "transparent", color: input.trim() ? T.accent : T.textFaint, fontSize: 15, cursor: input.trim() ? "pointer" : "default", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>↗</button>
        </div>
      </div>
    </div>
  );

  const supernova = <Supernova captured={captured} name={name} relationship={relationship} isMobile={isMobile} email={email} onEmail={setEmail} onSubmit={submitBrief} submitting={submitting} />;

  /* ═══ RENDER ═══ */
  return (
    <div style={{ width: "100vw", height: "100vh", overflow: "hidden", background: T.bg, fontFamily: '"Outfit", sans-serif', display: "flex", flexDirection: "column" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;1,300;1,400&family=Outfit:wght@300;400;500;600&display=swap');
        html,body{margin:0;padding:0;overflow:hidden;background:${T.bg}}
        *{box-sizing:border-box}
        @keyframes fadeUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}
        @keyframes pulse{0%,100%{opacity:1}50%{opacity:0.3}}
        @keyframes twinkle{0%,100%{opacity:0.25}50%{opacity:1}}
        @keyframes breathe{0%,100%{transform:translate(-50%,-50%) scale(1);opacity:0.85}50%{transform:translate(-50%,-50%) scale(1.07);opacity:1}}
        @keyframes glowtab{0%,100%{box-shadow:0 0 0 rgba(201,169,110,0)}50%{box-shadow:0 0 14px rgba(201,169,110,0.5)}}
        @keyframes grow{from{transform:scaleX(0);opacity:0}to{transform:scaleX(1);opacity:0.85}}
        @keyframes coreflare{0%,100%{opacity:0.85;transform:translate(-50%,-50%) scale(1)}50%{opacity:1;transform:translate(-50%,-50%) scale(1.04)}}
        ::-webkit-scrollbar{width:2px}
        ::-webkit-scrollbar-thumb{background:${T.accent}40;border-radius:4px}
        textarea:focus,button:focus,input:focus{outline:none}
        input::placeholder,textarea::placeholder{color:${T.textFaint}}
      `}</style>

      {/* ═══ HEADER (safe-area aware) ═══ */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: isMobile ? "calc(env(safe-area-inset-top, 0px) + 12px) 16px 12px" : "14px 28px", borderBottom: `1px solid ${T.border}`, background: T.panel, zIndex: 20, flexShrink: 0 }}>
        <div style={{ fontSize: isMobile ? 15 : 18, fontFamily: '"Cormorant Garamond", serif', fontStyle: "italic", color: T.text }}>Yours Truly</div>
        <button onClick={() => setIsDark(!isDark)} style={{ width: 34, height: 34, borderRadius: 10, border: "none", background: T.toggle.bg, color: T.toggle.color, fontSize: 16, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>{isDark ? "☀" : "☽"}</button>
      </div>

      {/* ═══ MOBILE TAB BAR ═══ */}
      {phase === "chat" && isMobile && (
        <div style={{ display: "flex", gap: 8, padding: "10px 16px", borderBottom: `1px solid ${T.border}`, background: T.panel, flexShrink: 0 }}>
          {[
            { id: "chat", label: "Chat" },
            { id: "star", label: name ? `✦ ${name}` : "✦ Star" },
          ].map(t => {
            const active = mobileTab === t.id;
            const glow = t.id === "star" && enoughCaptured && mobileTab !== "star";
            return (
              <button key={t.id} onClick={() => setMobileTab(t.id)} style={{
                flex: 1, padding: "9px", borderRadius: 12, cursor: "pointer", fontSize: 13, fontWeight: active ? 600 : 400, fontFamily: '"Outfit", sans-serif',
                border: active ? `1px solid ${T.gold}0.4)` : `1px solid ${T.border}`,
                background: active ? `${T.gold}0.12)` : "transparent",
                color: active ? T.accent : T.textMuted,
                animation: glow ? "glowtab 1.8s ease-in-out infinite" : "none",
                position: "relative",
              }}>
                {t.label}
                {t.id === "star" && captured.objects.length > 0 && (
                  <span style={{ marginLeft: 6, fontSize: 10, color: active ? T.accent : T.textMuted, opacity: 0.8 }}>{captured.objects.length}</span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* ═══ MAIN ═══ */}
      <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: isMobile ? "column" : "row", overflow: "hidden" }}>
        {phase === "chat" && (
          isMobile
            ? (mobileTab === "chat" ? chatColumn : supernova)
            : <>{chatColumn}{supernova}</>
        )}

        {/* ═══ DONE ═══ */}
        {phase === "done" && (
          <div style={{ flex: 1, position: "relative", background: SKY, display: "flex", alignItems: "center", justifyContent: "center", padding: "32px 24px", overflow: "hidden" }}>
            {STARS.map((s, i) => (
              <div key={i} style={{ position: "absolute", top: s.t, left: s.l, width: s.s, height: s.s, borderRadius: "50%", background: "rgba(255,240,210,0.9)", boxShadow: "0 0 6px rgba(255,220,170,0.7)", animation: `twinkle 3.4s ease-in-out ${s.d}s infinite` }} />
            ))}
            <div style={{ maxWidth: 420, textAlign: "center", position: "relative", zIndex: 2, animation: "fadeUp 0.6s ease" }}>
              <div style={{ fontSize: 40, marginBottom: 14, filter: "drop-shadow(0 0 20px rgba(255,200,140,0.8))" }}>✦</div>
              <div style={{ fontSize: 30, fontFamily: '"Cormorant Garamond", serif', fontStyle: "italic", fontWeight: 300, color: "#fff7e6", textShadow: "0 0 24px rgba(255,205,140,0.7)", marginBottom: 12 }}>{name ? `${name}'s` : "Their"} star is set</div>
              <div style={{ fontSize: 13, color: "rgba(255,235,210,0.7)", lineHeight: 1.7, marginBottom: 8 }}>Your sculptor is reading the constellation now. We'll email the finished piece to <span style={{ color: "#ffd9a0" }}>{email}</span>.</div>
              <div style={{ marginTop: 26, fontSize: 10, color: "rgba(255,225,190,0.4)", fontFamily: '"Cormorant Garamond", serif', fontStyle: "italic" }}>Yours Truly · One of one</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
